import React, { useState, useMemo } from "react";
import styles from "./styles.module.scss";
import { SortArrows } from "../SortArrows";
import { UpArrow, DownArrow } from "../../Styles/Icons/DesignSystem";
import ReasonBadge from "../ReasonBadge";
import { sortRecords, formatRangeLabel, safeFormatISO } from "../../Pages/InstallPerformance/utils";
import type { Granularity } from "../../Pages/InstallPerformance/utils";
import type { InstallRecordType } from "@intelligentlilli/api-layer";

type SortField = "userName" | "timestamp" | "reason";
type SortDirection = "asc" | "desc";

interface UnsuccessfulInstallsTableProps {
  records: InstallRecordType[];
  range?: { start: string; end: string };
  granularity?: Granularity;
}

const UnsuccessfulInstallsTable = ({
  records,
  range,
  granularity,
}: UnsuccessfulInstallsTableProps) => {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const sortedRecords = useMemo(() => {
    if (!sortField) return records;
    return sortRecords(records, sortField, sortDirection);
  }, [records, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return <SortArrows />;
    return sortDirection === "asc" ? <UpArrow /> : <DownArrow />;
  };

  const subtitle = range && granularity
    ? `${records.length} record${records.length !== 1 ? "s" : ""} — ${formatRangeLabel(range, granularity)}`
    : `${records.length} record${records.length !== 1 ? "s" : ""}`;

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Unsuccessful installs</h2>
      <p className={styles.subtitle}>{subtitle}</p>

      {records.length === 0 ? (
        <div className={styles.empty}>
          No unsuccessful installs for this period
        </div>
      ) : (
        <div className={styles.table_wrapper}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.table_header}>
                <th className={styles.th}>Resident name</th>
                <th
                  className={`${styles.th} ${styles.th_sortable}`}
                  onClick={() => handleSort("userName")}
                >
                  <div className={styles.th_content}>
                    <span>Staff name</span>
                    {renderSortIndicator("userName")}
                  </div>
                </th>
                <th
                  className={`${styles.th} ${styles.th_sortable}`}
                  onClick={() => handleSort("timestamp")}
                >
                  <div className={styles.th_content}>
                    <span>Date</span>
                    {renderSortIndicator("timestamp")}
                  </div>
                </th>
                <th
                  className={`${styles.th} ${styles.th_sortable}`}
                  onClick={() => handleSort("reason")}
                >
                  <div className={styles.th_content}>
                    <span>Reason</span>
                    {renderSortIndicator("reason")}
                  </div>
                </th>
                <th className={styles.th}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {sortedRecords.map((record, index) => (
                <tr key={`${record.serviceUserId}-${record.timestamp}-${index}`} className={styles.table_row}>
                  <td className={`${styles.td} ${styles.td_name}`}>
                    {record.serviceUserName || "\u2014"}
                  </td>
                  <td className={styles.td}>{record.userName}</td>
                  <td className={styles.td}>
                    {safeFormatISO(record.timestamp, "dd MMM yyyy")}
                  </td>
                  <td className={styles.td}>
                    <ReasonBadge reason={record.reason} />
                  </td>
                  <td className={styles.td}>{record.context || "\u2014"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UnsuccessfulInstallsTable;
